import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Smartphone } from 'lucide-react';
import { PhoneInput } from '@/components/PhoneInput';
import { OtpVerificationInput } from './OtpVerificationInput';
import { useSendMfaOtp, useVerifyMfaOtp, useEnrollOtpMfa } from '@/hooks/useOtpMFA';
import { supabase } from '@/integrations/supabase/client';

interface SmsMfaEnrollmentProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

type Step = 'phone' | 'verify';

export function SmsMfaEnrollment({ onSuccess, onCancel }: SmsMfaEnrollmentProps) {
  const { t } = useTranslation('common');
  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+1');
  const [isLoadingPhone, setIsLoadingPhone] = useState(true);

  const sendOtp = useSendMfaOtp();
  const _verifyOtp = useVerifyMfaOtp();
  const enrollMfa = useEnrollOtpMfa();

  // Load user's phone from profile
  useEffect(() => {
    const loadPhone = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('phone, country_code')
          .eq('user_id', user.id)
          .single();
        
        if (profile?.phone) {
          setPhone(profile.phone);
        }
        if (profile?.country_code) {
          setCountryCode(profile.country_code);
        }
      }
      setIsLoadingPhone(false);
    };
    loadPhone();
  }, []);

  const fullPhone = `${countryCode}${phone.replace(/\D/g, '')}`;

  const handleSendCode = async () => {
    if (!phone) return;
    try {
      await sendOtp.mutateAsync({ method: 'sms', destination: fullPhone });
      setStep('verify');
    } catch (_error) {
      // Error handled by mutation
    }
  };

  const handleVerify = async (code: string) => {
    try {
      // Combined verify + enroll in single call to avoid race conditions
      await enrollMfa.mutateAsync({ method: 'sms', destination: fullPhone, code });
      onSuccess?.();
    } catch (_error) {
      // Error handled by mutation
    }
  };

  if (step === 'verify') {
    return (
      <OtpVerificationInput
        title={t('mfa.verifyPhone', 'Verify your phone')}
        description={t('mfa.enterSmsCode', `Enter the 6-digit code sent to ${fullPhone}`)}
        onVerify={handleVerify}
        onBack={() => setStep('phone')}
        onResend={handleSendCode}
        isPending={enrollMfa.isPending}
      />
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Smartphone className="h-5 w-5 text-primary" />
          {t('mfa.smsSetup', 'Set up SMS Verification')}
        </CardTitle>
        <CardDescription>
          {t('mfa.smsSetupDescription', 'Receive a verification code via SMS each time you sign in')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>{t('auth.phoneNumber', 'Phone Number')}</Label>
          <PhoneInput
            countryCode={countryCode}
            phoneNumber={phone}
            onCountryCodeChange={setCountryCode}
            onPhoneNumberChange={setPhone}
            disabled={isLoadingPhone}
          />
          <p className="text-sm text-muted-foreground">
            {t('mfa.smsNote', 'We will send verification codes to this phone number')}
          </p>
        </div>

        <div className="flex gap-2 pt-4">
          {onCancel && (
            <Button variant="outline" onClick={onCancel}>
              {t('common.cancel', 'Cancel')}
            </Button>
          )}
          <Button 
            onClick={handleSendCode} 
            disabled={!phone || sendOtp.isPending || isLoadingPhone}
          >
            {sendOtp.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Smartphone className="h-4 w-4 mr-2" />
            )}
            {t('mfa.sendVerificationCode', 'Send Verification Code')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
